import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowLeft, ShieldCheck, Upload, Check, Trash2, Plus } from 'lucide-react';
import { z } from 'zod';
import {
  api,
  ApiError,
  type BuyerKycSubmissionDTO,
  type BuyerKycUploadUrlDTO,
  type InitiateBuyerKycBody,
} from '../lib/api.ts';
import { readBuyerSession } from '../lib/buyer-session.ts';
import { COUNTRY_CODES } from '../lib/country-codes.ts';

// Multi-step KYC/KYB form. Branches on entityType (individual vs
// business) — Blindpay's /receivers endpoint accepts both shapes but
// with very different field sets.
//
// Step list — entityType chosen up front, ToS next, then the path
// diverges:
//
//   Individual: tos -> personal -> address -> documents -> review
//   Business:   tos -> business -> bizAddress -> owners -> bizDocuments -> review
//
// Form state survives the ToS redirect via sessionStorage. Files upload
// directly from the browser to GCS via the signed URLs the API mints;
// owner-scoped files use ownerIndex so they don't collide.

// ISO 3166-1 alpha-2 — uppercased two-letter codes only. Form inputs
// uppercase on change so accepting lowercase pre-submit was a no-op
// inconsistency; tighten to match the submit transformer.
const COUNTRY_RE = /^[A-Z]{2}$/;
const STORAGE_KEY = (publicId: string): string => `caratuva.kyc.draft.${publicId}`;
const ID_BACK_REQUIRED: ReadonlySet<string> = new Set(['ID_CARD', 'DRIVERS']);
const MAX_FILE_BYTES = 8 * 1024 * 1024;
const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'] as const;
const ID_DOC_TYPES = ['PASSPORT', 'ID_CARD', 'DRIVERS'] as const;
const OWNER_ROLES = ['beneficial_controlling', 'beneficial_owner', 'controlling_person'] as const;
const PROOF_OF_ADDRESS_TYPES = [
  'UTILITY_BILL',
  'BANK_STATEMENT',
  'RENTAL_AGREEMENT',
  'TAX_DOCUMENT',
  'GOVERNMENT_CORRESPONDENCE',
] as const;
const TAX_TYPES = ['SSN', 'ITIN'] as const;

type FileKind =
  | 'selfie'
  | 'id_doc_front'
  | 'id_doc_back'
  | 'proof_of_address'
  | 'incorporation_doc'
  | 'proof_of_ownership_doc';

type EntityType = 'individual' | 'business';

type OwnerState = {
  role: '' | (typeof OWNER_ROLES)[number];
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  taxId: string;
  taxType: '' | (typeof TAX_TYPES)[number];
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvinceRegion: string;
  postalCode: string;
  country: string;
  phone: string;
  idDocCountry: string;
  idDocType: '' | (typeof ID_DOC_TYPES)[number];
  idDocNumber: string;
  selfieFile: string;
  idDocFrontFile: string;
  idDocBackFile: string;
  proofOfAddressDocType: '' | (typeof PROOF_OF_ADDRESS_TYPES)[number];
  proofOfAddressDocFile: string;
};

const EMPTY_OWNER: OwnerState = {
  role: '',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  taxId: '',
  taxType: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateProvinceRegion: '',
  postalCode: '',
  country: '',
  phone: '',
  idDocCountry: '',
  idDocType: '',
  idDocNumber: '',
  selfieFile: '',
  idDocFrontFile: '',
  idDocBackFile: '',
  proofOfAddressDocType: '',
  proofOfAddressDocFile: '',
};

type FormState = {
  entityType: EntityType;
  // Shared
  buyerEmail: string;
  buyerCountry: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvinceRegion: string;
  postalCode: string;
  // Individual
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  taxId: string;
  idDocCountry: string;
  idDocType: '' | (typeof ID_DOC_TYPES)[number];
  idDocNumber: string;
  selfieFile: string;
  idDocFrontFile: string;
  idDocBackFile: string;
  // Business
  legalName: string;
  formationDate: string;
  website: string;
  businessTaxId: string;
  incorporationDocFile: string;
  proofOfOwnershipDocFile: string;
  proofOfAddressDocType: '' | (typeof PROOF_OF_ADDRESS_TYPES)[number];
  proofOfAddressDocFile: string;
  owners: OwnerState[];
};

const EMPTY_FORM: FormState = {
  entityType: 'individual',
  buyerEmail: '',
  buyerCountry: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateProvinceRegion: '',
  postalCode: '',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  taxId: '',
  idDocCountry: '',
  idDocType: '',
  idDocNumber: '',
  selfieFile: '',
  idDocFrontFile: '',
  idDocBackFile: '',
  legalName: '',
  formationDate: '',
  website: '',
  businessTaxId: '',
  incorporationDocFile: '',
  proofOfOwnershipDocFile: '',
  proofOfAddressDocType: 'BANK_STATEMENT',
  proofOfAddressDocFile: '',
  owners: [{ ...EMPTY_OWNER }],
};

type FormErrors = Partial<Record<string, string>>;

type StepId =
  | 'entityType'
  | 'tos'
  | 'personal'
  | 'address'
  | 'documents'
  | 'business'
  | 'businessAddress'
  | 'owners'
  | 'businessDocuments'
  | 'review';

const INDIVIDUAL_STEPS: readonly StepId[] = [
  'entityType',
  'tos',
  'personal',
  'address',
  'documents',
  'review',
] as const;
const BUSINESS_STEPS: readonly StepId[] = [
  'entityType',
  'tos',
  'business',
  'businessAddress',
  'owners',
  'businessDocuments',
  'review',
] as const;

const KycSearch = z.object({
  // Blindpay's ToS-acceptance redirect sends the buyer back to this
  // route with `?tos_id=...`. We persist it server-side and strip the
  // query param, leaving `?tos=accepted` so a subsequent reload skips
  // the ToS step without re-running the API call.
  tos_id: z.string().optional(),
  tos: z.literal('accepted').optional(),
});

function getCountryOptions(locale: string): { code: string; name: string }[] {
  const display = new Intl.DisplayNames([locale], { type: 'region' });
  return COUNTRY_CODES.map((code) => ({ code, name: display.of(code) ?? code })).sort((a, b) =>
    a.name.localeCompare(b.name, locale),
  );
}

function isIso8601Date(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function ageYears(yyyyMmDd: string): number {
  const dob = new Date(`${yyyyMmDd}T00:00:00Z`);
  const now = new Date();
  let years = now.getUTCFullYear() - dob.getUTCFullYear();
  const m = now.getUTCMonth() - dob.getUTCMonth();
  if (m < 0 || (m === 0 && now.getUTCDate() < dob.getUTCDate())) years--;
  return years;
}

function loadDraft(publicId: string): FormState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY(publicId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return { ...EMPTY_FORM, ...(parsed as Partial<FormState>) };
  } catch {
    return null;
  }
}

function saveDraft(publicId: string, form: FormState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY(publicId), JSON.stringify(form));
  } catch {
    // sessionStorage is best-effort; ignore quota errors.
  }
}

function clearDraft(publicId: string): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY(publicId));
  } catch {
    // ignore
  }
}

function KycPage(): React.ReactElement {
  const { publicId } = Route.useParams();
  const search = Route.useSearch();
  const intl = useIntl();
  const navigate = useNavigate();
  const session = readBuyerSession();

  const existing = useQuery<BuyerKycSubmissionDTO, ApiError>({
    queryKey: ['buyer-kyc', session?.buyerId ?? null],
    queryFn: () => api.getBuyerKyc(session!.buyerId),
    enabled: Boolean(session?.buyerId),
    retry: false,
  });

  const submission = existing.data;
  const hasActive = submission?.status === 'verifying' || submission?.status === 'approved';

  const [form, setForm] = useState<FormState>(() => loadDraft(publicId) ?? EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const steps = form.entityType === 'business' ? BUSINESS_STEPS : INDIVIDUAL_STEPS;
  // Initial step: 0 (entity type) on a clean load, or 1 (ToS step) when
  // the buyer is coming back from acceptance — they pick up where they
  // left off and the ToS step renders the post-acceptance UI.
  const [stepIndex, setStepIndex] = useState<number>(() =>
    search.tos === 'accepted' || search.tos_id ? 1 : 0,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [tosAccepted, setTosAccepted] = useState<boolean>(search.tos === 'accepted');
  // Guard against React 18 StrictMode double-invoke: only call the API once
  // per tos_id even if the effect fires twice.
  const tosRecordedRef = useRef(false);

  useEffect(() => {
    saveDraft(publicId, form);
  }, [publicId, form]);

  const countryOptions = useMemo(() => getCountryOptions(intl.locale), [intl.locale]);

  const submit = useMutation<BuyerKycSubmissionDTO, ApiError, InitiateBuyerKycBody>({
    mutationFn: (input) => api.submitBuyerKyc(input),
    onSuccess: () => {
      clearDraft(publicId);
      void navigate({
        to: '/r/$publicId/tracking',
        params: { publicId },
        replace: true,
      });
    },
    onError: (err) => {
      setSubmitError(err.message);
    },
  });

  // Handle the Blindpay redirect: if `?tos_id=...` is present, POST it
  // to the API once, then strip the query and flip to `?tos=accepted`
  // so a reload doesn't re-submit. The buyer stays on the ToS step
  // until they hit Continue.
  useEffect(() => {
    if (!search.tos_id || tosAccepted || tosRecordedRef.current) return;
    tosRecordedRef.current = true;
    const tosId = search.tos_id;
    void (async () => {
      try {
        await api.recordKycTosAcceptance({ tosId });
        setTosAccepted(true);
        await navigate({
          to: '/r/$publicId/kyc',
          params: { publicId },
          search: { tos: 'accepted' },
          replace: true,
        });
      } catch (err) {
        tosRecordedRef.current = false;
        // Surface the API failure inline; the user can retry from the
        // ToS step button (which still shows because tosAccepted is false).
        setSubmitError(err instanceof ApiError ? err.message : 'Could not record ToS acceptance.');
      }
    })();
  }, [search.tos_id, tosAccepted, navigate, publicId]);

  // Already approved → go straight to on-ramp (preserves prior behavior).
  useEffect(() => {
    if (submission?.status === 'approved') {
      void navigate({
        to: '/r/$publicId/on-ramp',
        params: { publicId },
        replace: true,
      });
    }
  }, [submission?.status, publicId, navigate]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateOwner(index: number, partial: Partial<OwnerState>): void {
    setForm((prev) => ({
      ...prev,
      owners: prev.owners.map((o, i) => (i === index ? { ...o, ...partial } : o)),
    }));
  }

  function addOwner(): void {
    setForm((prev) => ({ ...prev, owners: [...prev.owners, { ...EMPTY_OWNER }] }));
  }

  function removeOwner(index: number): void {
    setForm((prev) => {
      if (prev.owners.length <= 1) return prev;
      return { ...prev, owners: prev.owners.filter((_, i) => i !== index) };
    });
  }

  function validateStep(step: StepId, f: FormState): FormErrors {
    const next: FormErrors = {};
    const required = (key: string, value: unknown): void => {
      if (!String(value ?? '').trim()) next[key] = intl.formatMessage({ id: 'kyc.error.required' });
    };
    if (step === 'personal') {
      if (!f.buyerEmail) next.buyerEmail = intl.formatMessage({ id: 'kyc.error.required' });
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.buyerEmail))
        next.buyerEmail = intl.formatMessage({ id: 'kyc.error.email' });
      required('firstName', f.firstName);
      required('lastName', f.lastName);
      if (!f.dateOfBirth) next.dateOfBirth = intl.formatMessage({ id: 'kyc.error.required' });
      else if (!isIso8601Date(f.dateOfBirth))
        next.dateOfBirth = intl.formatMessage({ id: 'kyc.error.dateOfBirth' });
      else if (ageYears(f.dateOfBirth) < 18)
        next.dateOfBirth = intl.formatMessage({ id: 'kyc.error.minor' });
      required('taxId', f.taxId);
      if (!f.buyerCountry) next.buyerCountry = intl.formatMessage({ id: 'kyc.error.required' });
      else if (!COUNTRY_RE.test(f.buyerCountry))
        next.buyerCountry = intl.formatMessage({ id: 'kyc.error.country' });
    }
    if (step === 'address') {
      required('addressLine1', f.addressLine1);
      required('city', f.city);
      required('stateProvinceRegion', f.stateProvinceRegion);
      required('postalCode', f.postalCode);
    }
    if (step === 'documents') {
      if (!f.idDocCountry) next.idDocCountry = intl.formatMessage({ id: 'kyc.error.required' });
      else if (!COUNTRY_RE.test(f.idDocCountry))
        next.idDocCountry = intl.formatMessage({ id: 'kyc.error.country' });
      if (!f.idDocType) next.idDocType = intl.formatMessage({ id: 'kyc.error.required' });
      if (!f.selfieFile) next.selfieFile = intl.formatMessage({ id: 'kyc.error.required' });
      if (!f.idDocFrontFile) next.idDocFrontFile = intl.formatMessage({ id: 'kyc.error.required' });
      if (f.idDocType && ID_BACK_REQUIRED.has(f.idDocType) && !f.idDocBackFile) {
        next.idDocBackFile = intl.formatMessage({ id: 'kyc.error.required' });
      }
    }
    if (step === 'business') {
      if (!f.buyerEmail) next.buyerEmail = intl.formatMessage({ id: 'kyc.error.required' });
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.buyerEmail))
        next.buyerEmail = intl.formatMessage({ id: 'kyc.error.email' });
      required('legalName', f.legalName);
      if (!f.formationDate) next.formationDate = intl.formatMessage({ id: 'kyc.error.required' });
      else if (!isIso8601Date(f.formationDate))
        next.formationDate = intl.formatMessage({ id: 'kyc.error.dateOfBirth' });
      required('businessTaxId', f.businessTaxId);
      required('website', f.website);
      if (!f.buyerCountry) next.buyerCountry = intl.formatMessage({ id: 'kyc.error.required' });
      else if (!COUNTRY_RE.test(f.buyerCountry))
        next.buyerCountry = intl.formatMessage({ id: 'kyc.error.country' });
    }
    if (step === 'businessAddress') {
      required('addressLine1', f.addressLine1);
      required('city', f.city);
      required('stateProvinceRegion', f.stateProvinceRegion);
      required('postalCode', f.postalCode);
    }
    if (step === 'owners') {
      f.owners.forEach((o, i) => {
        const prefix = `owners.${i}.`;
        if (!o.role) next[`${prefix}role`] = intl.formatMessage({ id: 'kyc.error.required' });
        required(`${prefix}firstName`, o.firstName);
        required(`${prefix}lastName`, o.lastName);
        if (!o.dateOfBirth)
          next[`${prefix}dateOfBirth`] = intl.formatMessage({ id: 'kyc.error.required' });
        else if (!isIso8601Date(o.dateOfBirth))
          next[`${prefix}dateOfBirth`] = intl.formatMessage({ id: 'kyc.error.dateOfBirth' });
        else if (ageYears(o.dateOfBirth) < 18)
          next[`${prefix}dateOfBirth`] = intl.formatMessage({ id: 'kyc.error.minor' });
        required(`${prefix}taxId`, o.taxId);
        if (o.country === 'US' && !o.taxType)
          next[`${prefix}taxType`] = intl.formatMessage({ id: 'kyc.error.required' });
        required(`${prefix}addressLine1`, o.addressLine1);
        required(`${prefix}city`, o.city);
        required(`${prefix}stateProvinceRegion`, o.stateProvinceRegion);
        required(`${prefix}postalCode`, o.postalCode);
        if (!o.country) next[`${prefix}country`] = intl.formatMessage({ id: 'kyc.error.required' });
        else if (!COUNTRY_RE.test(o.country))
          next[`${prefix}country`] = intl.formatMessage({ id: 'kyc.error.country' });
        if (!o.idDocCountry)
          next[`${prefix}idDocCountry`] = intl.formatMessage({ id: 'kyc.error.required' });
        if (!o.idDocType)
          next[`${prefix}idDocType`] = intl.formatMessage({ id: 'kyc.error.required' });
        if (!o.selfieFile)
          next[`${prefix}selfieFile`] = intl.formatMessage({ id: 'kyc.error.required' });
        if (!o.idDocFrontFile)
          next[`${prefix}idDocFrontFile`] = intl.formatMessage({ id: 'kyc.error.required' });
        if (o.idDocType && ID_BACK_REQUIRED.has(o.idDocType) && !o.idDocBackFile) {
          next[`${prefix}idDocBackFile`] = intl.formatMessage({ id: 'kyc.error.required' });
        }
        if (!o.proofOfAddressDocType)
          next[`${prefix}proofOfAddressDocType`] = intl.formatMessage({
            id: 'kyc.error.required',
          });
        if (!o.proofOfAddressDocFile)
          next[`${prefix}proofOfAddressDocFile`] = intl.formatMessage({
            id: 'kyc.error.required',
          });
      });
    }
    if (step === 'businessDocuments') {
      if (!f.incorporationDocFile)
        next.incorporationDocFile = intl.formatMessage({ id: 'kyc.error.required' });
      if (!f.proofOfOwnershipDocFile)
        next.proofOfOwnershipDocFile = intl.formatMessage({ id: 'kyc.error.required' });
      if (!f.proofOfAddressDocType)
        next.proofOfAddressDocType = intl.formatMessage({ id: 'kyc.error.required' });
      if (!f.proofOfAddressDocFile)
        next.proofOfAddressDocFile = intl.formatMessage({ id: 'kyc.error.required' });
    }
    return next;
  }

  function goNext(): void {
    const step = steps[stepIndex];
    if (!step) return;
    const e = validateStep(step, form);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setStepIndex((idx) => Math.min(idx + 1, steps.length - 1));
  }

  function goBack(): void {
    setErrors({});
    setStepIndex((idx) => Math.max(idx - 1, 0));
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setSubmitError(null);
    const body =
      form.entityType === 'business' ? buildBusinessBody(form) : buildIndividualBody(form);
    submit.mutate(body);
  }

  if (existing.isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10 text-[var(--color-navy-600)]">
        <FormattedMessage id="common.loading" />
      </div>
    );
  }

  const currentStep = steps[stepIndex]!;

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <Link
        to="/r/$publicId"
        params={{ publicId }}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-navy-600)] hover:text-[var(--color-navy-900)]"
      >
        <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
        <FormattedMessage id="tracking.cta.back" />
      </Link>

      <div className="mt-4 rounded-xl border border-[var(--color-navy-100)] bg-white p-6 shadow-sm">
        {hasActive ? (
          <ReviewState submission={submission!} publicId={publicId} />
        ) : (
          <>
            <header>
              <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--color-navy-900)]">
                <ShieldCheck size={20} strokeWidth={1.5} aria-hidden="true" />
                <FormattedMessage id="kyc.heading" />
              </h1>
              <p className="mt-1 text-sm text-[var(--color-navy-600)]">
                <FormattedMessage id="kyc.subheading" />
              </p>
              <StepIndicator steps={steps} current={stepIndex} />
            </header>

            <form onSubmit={onSubmit} noValidate className="mt-6 space-y-4">
              {currentStep === 'entityType' && (
                <EntityTypeStep
                  value={form.entityType}
                  onChange={(t) => {
                    update('entityType', t);
                    setStepIndex(1);
                  }}
                />
              )}
              {currentStep === 'tos' && (
                <TosStep
                  publicId={publicId}
                  accepted={tosAccepted}
                  onAdvance={() => setStepIndex(stepIndex + 1)}
                />
              )}
              {currentStep === 'personal' && (
                <PersonalStep
                  form={form}
                  errors={errors}
                  countryOptions={countryOptions}
                  onChange={update}
                />
              )}
              {currentStep === 'address' && (
                <AddressStep form={form} errors={errors} onChange={update} />
              )}
              {currentStep === 'documents' && (
                <DocumentsStep
                  form={form}
                  errors={errors}
                  countryOptions={countryOptions}
                  onChange={update}
                />
              )}
              {currentStep === 'business' && (
                <BusinessInfoStep
                  form={form}
                  errors={errors}
                  countryOptions={countryOptions}
                  onChange={update}
                />
              )}
              {currentStep === 'businessAddress' && (
                <AddressStep form={form} errors={errors} onChange={update} />
              )}
              {currentStep === 'owners' && (
                <OwnersStep
                  form={form}
                  errors={errors}
                  countryOptions={countryOptions}
                  onUpdate={updateOwner}
                  onAdd={addOwner}
                  onRemove={removeOwner}
                />
              )}
              {currentStep === 'businessDocuments' && (
                <BusinessDocumentsStep form={form} errors={errors} onChange={update} />
              )}
              {currentStep === 'review' && (
                <ReviewStep form={form} countryOptions={countryOptions} />
              )}

              {submitError && (
                <p className="text-sm text-[var(--color-danger)]" role="alert">
                  {submitError}
                </p>
              )}

              {currentStep !== 'tos' && currentStep !== 'entityType' && (
                <div className="flex items-center justify-between gap-3 pt-2">
                  {stepIndex > 0 ? (
                    <button
                      type="button"
                      onClick={goBack}
                      className="rounded-md border border-[var(--color-navy-200)] px-4 py-2 text-sm text-[var(--color-navy-800)] hover:bg-[var(--color-navy-50)]"
                    >
                      <FormattedMessage id="kyc.back" />
                    </button>
                  ) : (
                    <span />
                  )}
                  {currentStep === 'review' ? (
                    <button
                      type="submit"
                      disabled={submit.isPending}
                      className="rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {submit.isPending ? (
                        <FormattedMessage id="kyc.submit.pending" />
                      ) : (
                        <FormattedMessage id="kyc.submit" />
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={goNext}
                      className="rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)]"
                    >
                      <FormattedMessage id="kyc.next" />
                    </button>
                  )}
                </div>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function buildIndividualBody(form: FormState): InitiateBuyerKycBody {
  return {
    entityType: 'individual',
    buyerEmail: form.buyerEmail,
    firstName: form.firstName,
    lastName: form.lastName,
    // date-only (YYYY-MM-DD); the BlindPay client widens to a datetime.
    dateOfBirth: form.dateOfBirth,
    taxId: form.taxId,
    buyerCountry: form.buyerCountry.toUpperCase(),
    ...(form.phone ? { phone: form.phone } : {}),
    addressLine1: form.addressLine1,
    ...(form.addressLine2 ? { addressLine2: form.addressLine2 } : {}),
    city: form.city,
    stateProvinceRegion: form.stateProvinceRegion,
    postalCode: form.postalCode,
    idDocCountry: form.idDocCountry.toUpperCase(),
    ...(form.idDocType ? { idDocType: form.idDocType } : {}),
    ...(form.idDocNumber ? { idDocNumber: form.idDocNumber } : {}),
    selfieFile: form.selfieFile,
    idDocFrontFile: form.idDocFrontFile,
    ...(form.idDocBackFile ? { idDocBackFile: form.idDocBackFile } : {}),
  };
}

function buildBusinessBody(form: FormState): InitiateBuyerKycBody {
  return {
    entityType: 'business',
    buyerEmail: form.buyerEmail,
    legalName: form.legalName,
    // Send the calendar date (YYYY-MM-DD). Our API validates date-only and the
    // BlindPay client widens it to a datetime at the wire boundary.
    formationDate: form.formationDate,
    taxId: form.businessTaxId,
    website: form.website,
    buyerCountry: form.buyerCountry.toUpperCase(),
    ...(form.phone ? { phone: form.phone } : {}),
    addressLine1: form.addressLine1,
    ...(form.addressLine2 ? { addressLine2: form.addressLine2 } : {}),
    city: form.city,
    stateProvinceRegion: form.stateProvinceRegion,
    postalCode: form.postalCode,
    incorporationDocFile: form.incorporationDocFile,
    proofOfOwnershipDocFile: form.proofOfOwnershipDocFile,
    proofOfAddressDocType: form.proofOfAddressDocType as
      | 'UTILITY_BILL'
      | 'BANK_STATEMENT'
      | 'RENTAL_AGREEMENT'
      | 'TAX_DOCUMENT'
      | 'GOVERNMENT_CORRESPONDENCE',
    proofOfAddressDocFile: form.proofOfAddressDocFile,
    owners: form.owners.map((o) => ({
      role: o.role as 'beneficial_owner' | 'beneficial_controlling' | 'controlling_person',
      firstName: o.firstName,
      lastName: o.lastName,
      // date-only (YYYY-MM-DD) to satisfy the API schema; the BlindPay client
      // widens to a datetime before sending to BlindPay.
      dateOfBirth: o.dateOfBirth,
      taxId: o.taxId,
      ...(o.taxType ? { taxType: o.taxType } : {}),
      addressLine1: o.addressLine1,
      ...(o.addressLine2 ? { addressLine2: o.addressLine2 } : {}),
      city: o.city,
      stateProvinceRegion: o.stateProvinceRegion,
      postalCode: o.postalCode,
      country: o.country.toUpperCase(),
      ...(o.phone ? { phone: o.phone } : {}),
      idDocCountry: o.idDocCountry.toUpperCase(),
      idDocType: o.idDocType as 'PASSPORT' | 'ID_CARD' | 'DRIVERS',
      ...(o.idDocNumber ? { idDocNumber: o.idDocNumber } : {}),
      selfieFile: o.selfieFile,
      idDocFrontFile: o.idDocFrontFile,
      ...(o.idDocBackFile ? { idDocBackFile: o.idDocBackFile } : {}),
      proofOfAddressDocType: o.proofOfAddressDocType as
        | 'UTILITY_BILL'
        | 'BANK_STATEMENT'
        | 'RENTAL_AGREEMENT'
        | 'TAX_DOCUMENT'
        | 'GOVERNMENT_CORRESPONDENCE',
      proofOfAddressDocFile: o.proofOfAddressDocFile,
    })),
  };
}

function StepIndicator({
  steps,
  current,
}: {
  steps: readonly StepId[];
  current: number;
}): React.ReactElement {
  return (
    <p className="mt-3 text-xs uppercase tracking-wide text-[var(--color-navy-500)]">
      <FormattedMessage
        id="kyc.step.indicator"
        values={{ current: current + 1, total: steps.length }}
      />
      {' · '}
      <FormattedMessage id={`kyc.step.${steps[current]}`} />
    </p>
  );
}

function EntityTypeStep({
  value,
  onChange,
}: {
  value: EntityType;
  onChange: (t: EntityType) => void;
}): React.ReactElement {
  return (
    <section className="space-y-4">
      <h2 className="text-base font-medium text-[var(--color-navy-900)]">
        <FormattedMessage id="kyc.entityType.heading" />
      </h2>
      <p className="text-sm text-[var(--color-navy-600)]">
        <FormattedMessage id="kyc.entityType.body" />
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <EntityCard
          selected={value === 'individual'}
          onClick={() => onChange('individual')}
          title={<FormattedMessage id="kyc.entityType.individual" />}
          body={<FormattedMessage id="kyc.entityType.individual.body" />}
        />
        <EntityCard
          selected={value === 'business'}
          onClick={() => onChange('business')}
          title={<FormattedMessage id="kyc.entityType.business" />}
          body={<FormattedMessage id="kyc.entityType.business.body" />}
        />
      </div>
    </section>
  );
}

function EntityCard({
  selected,
  onClick,
  title,
  body,
}: {
  selected: boolean;
  onClick: () => void;
  title: ReactNode;
  body: ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-lg border p-4 text-left transition ' +
        (selected
          ? 'border-[var(--color-navy-800)] bg-[var(--color-navy-50)]'
          : 'border-[var(--color-navy-200)] hover:bg-[var(--color-navy-50)]')
      }
    >
      <div className="text-sm font-medium text-[var(--color-navy-900)]">{title}</div>
      <p className="mt-1 text-xs text-[var(--color-navy-600)]">{body}</p>
    </button>
  );
}

function TosStep({
  publicId,
  accepted,
  onAdvance,
}: {
  publicId: string;
  accepted: boolean;
  onAdvance: () => void;
}): React.ReactElement {
  const [error, setError] = useState<string | null>(null);
  const startTos = useMutation({
    mutationFn: async () => {
      // Send the buyer back here with `?tos_id=...`; the route-level
      // effect picks that up and POSTs to /tos-accepted automatically.
      const redirectUrl = `${window.location.origin}/r/${encodeURIComponent(publicId)}/kyc`;
      const session = await api.createKycTosSession({ redirectUrl });
      window.location.assign(session.url);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not start ToS.');
    },
  });
  return (
    <section className="space-y-4">
      <h2 className="text-base font-medium text-[var(--color-navy-900)]">
        <FormattedMessage id="kyc.tos.heading" />
      </h2>
      <p className="text-sm text-[var(--color-navy-600)]">
        <FormattedMessage id="kyc.tos.body" />
      </p>
      {error && (
        <p className="text-sm text-[var(--color-danger)]" role="alert">
          {error}
        </p>
      )}
      {accepted && (
        <p className="text-sm text-[var(--color-success,green)]" role="status">
          <FormattedMessage id="kyc.tos.accepted" />
        </p>
      )}
      <div className="flex gap-3">
        {accepted ? (
          <button
            type="button"
            onClick={onAdvance}
            className="rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)]"
          >
            <FormattedMessage id="kyc.next" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => startTos.mutate()}
            disabled={startTos.isPending}
            className="rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {startTos.isPending ? (
              <FormattedMessage id="kyc.tos.cta.pending" />
            ) : (
              <FormattedMessage id="kyc.tos.cta" />
            )}
          </button>
        )}
      </div>
    </section>
  );
}

function PersonalStep({
  form,
  errors,
  countryOptions,
  onChange,
}: {
  form: FormState;
  errors: FormErrors;
  countryOptions: { code: string; name: string }[];
  onChange: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}): React.ReactElement {
  const intl = useIntl();
  return (
    <section className="space-y-4">
      <Field label={intl.formatMessage({ id: 'kyc.field.email.label' })} error={errors.buyerEmail}>
        <input
          type="email"
          required
          autoComplete="email"
          value={form.buyerEmail}
          onChange={(e) => onChange('buyerEmail', e.target.value)}
          className={inputClass(errors.buyerEmail)}
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label={intl.formatMessage({ id: 'kyc.field.firstName.label' })}
          error={errors.firstName}
        >
          <input
            type="text"
            required
            autoComplete="given-name"
            value={form.firstName}
            placeholder={intl.formatMessage({ id: 'kyc.field.firstName.placeholder' })}
            onChange={(e) => onChange('firstName', e.target.value)}
            className={inputClass(errors.firstName)}
          />
        </Field>
        <Field
          label={intl.formatMessage({ id: 'kyc.field.lastName.label' })}
          error={errors.lastName}
        >
          <input
            type="text"
            required
            autoComplete="family-name"
            value={form.lastName}
            placeholder={intl.formatMessage({ id: 'kyc.field.lastName.placeholder' })}
            onChange={(e) => onChange('lastName', e.target.value)}
            className={inputClass(errors.lastName)}
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label={intl.formatMessage({ id: 'kyc.field.dateOfBirth.label' })}
          error={errors.dateOfBirth}
        >
          <input
            type="date"
            required
            autoComplete="bday"
            value={form.dateOfBirth}
            onChange={(e) => onChange('dateOfBirth', e.target.value)}
            className={inputClass(errors.dateOfBirth)}
          />
        </Field>
        <Field
          label={intl.formatMessage({ id: 'kyc.field.taxId.label' })}
          error={errors.taxId}
          hint={intl.formatMessage({ id: 'kyc.field.taxId.hint' })}
        >
          <input
            type="text"
            required
            value={form.taxId}
            placeholder={intl.formatMessage({ id: 'kyc.field.taxId.placeholder' })}
            onChange={(e) => onChange('taxId', e.target.value)}
            className={inputClass(errors.taxId)}
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label={intl.formatMessage({ id: 'kyc.field.country.label' })}
          error={errors.buyerCountry}
        >
          <CountrySelect
            options={countryOptions}
            value={form.buyerCountry}
            onChange={(v) => onChange('buyerCountry', v)}
            error={errors.buyerCountry}
          />
        </Field>
        <Field label={intl.formatMessage({ id: 'kyc.field.phone.label' })} error={errors.phone}>
          <input
            type="tel"
            autoComplete="tel"
            value={form.phone}
            placeholder={intl.formatMessage({ id: 'kyc.field.phone.placeholder' })}
            onChange={(e) => onChange('phone', e.target.value)}
            className={inputClass(errors.phone)}
          />
        </Field>
      </div>
    </section>
  );
}

function AddressStep({
  form,
  errors,
  onChange,
}: {
  form: FormState;
  errors: FormErrors;
  onChange: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}): React.ReactElement {
  const intl = useIntl();
  return (
    <section className="space-y-4">
      <Field
        label={intl.formatMessage({ id: 'kyc.field.addressLine1.label' })}
        error={errors.addressLine1}
      >
        <input
          type="text"
          required
          autoComplete="address-line1"
          value={form.addressLine1}
          placeholder={intl.formatMessage({ id: 'kyc.field.addressLine1.placeholder' })}
          onChange={(e) => onChange('addressLine1', e.target.value)}
          className={inputClass(errors.addressLine1)}
        />
      </Field>
      <Field
        label={intl.formatMessage({ id: 'kyc.field.addressLine2.label' })}
        error={errors.addressLine2}
      >
        <input
          type="text"
          autoComplete="address-line2"
          value={form.addressLine2}
          placeholder={intl.formatMessage({ id: 'kyc.field.addressLine2.placeholder' })}
          onChange={(e) => onChange('addressLine2', e.target.value)}
          className={inputClass(errors.addressLine2)}
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={intl.formatMessage({ id: 'kyc.field.city.label' })} error={errors.city}>
          <input
            type="text"
            required
            autoComplete="address-level2"
            value={form.city}
            onChange={(e) => onChange('city', e.target.value)}
            className={inputClass(errors.city)}
          />
        </Field>
        <Field
          label={intl.formatMessage({ id: 'kyc.field.stateProvinceRegion.label' })}
          error={errors.stateProvinceRegion}
        >
          <input
            type="text"
            required
            autoComplete="address-level1"
            value={form.stateProvinceRegion}
            onChange={(e) => onChange('stateProvinceRegion', e.target.value)}
            className={inputClass(errors.stateProvinceRegion)}
          />
        </Field>
      </div>
      <Field
        label={intl.formatMessage({ id: 'kyc.field.postalCode.label' })}
        error={errors.postalCode}
      >
        <input
          type="text"
          required
          autoComplete="postal-code"
          value={form.postalCode}
          onChange={(e) => onChange('postalCode', e.target.value)}
          className={inputClass(errors.postalCode)}
        />
      </Field>
    </section>
  );
}

function DocumentsStep({
  form,
  errors,
  countryOptions,
  onChange,
}: {
  form: FormState;
  errors: FormErrors;
  countryOptions: { code: string; name: string }[];
  onChange: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}): React.ReactElement {
  const intl = useIntl();
  const showBack = form.idDocType === 'ID_CARD' || form.idDocType === 'DRIVERS';
  return (
    <section className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label={intl.formatMessage({ id: 'kyc.field.idDocCountry.label' })}
          error={errors.idDocCountry}
        >
          <CountrySelect
            options={countryOptions}
            value={form.idDocCountry}
            onChange={(v) => onChange('idDocCountry', v)}
            error={errors.idDocCountry}
          />
        </Field>
        <Field
          label={intl.formatMessage({ id: 'kyc.field.idDocType.label' })}
          error={errors.idDocType}
        >
          <select
            required
            value={form.idDocType}
            onChange={(e) => onChange('idDocType', e.target.value as FormState['idDocType'])}
            className={inputClass(errors.idDocType)}
          >
            <option value="">
              {intl.formatMessage({ id: 'kyc.field.idDocType.placeholder' })}
            </option>
            {ID_DOC_TYPES.map((t) => (
              <option key={t} value={t}>
                {intl.formatMessage({ id: `kyc.field.idDocType.${t.toLowerCase()}` })}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field
        label={intl.formatMessage({ id: 'kyc.field.idDocNumber.label' })}
        error={errors.idDocNumber}
      >
        <input
          type="text"
          value={form.idDocNumber}
          onChange={(e) => onChange('idDocNumber', e.target.value)}
          className={inputClass(errors.idDocNumber)}
        />
      </Field>
      <FileUpload
        kind="selfie"
        label={intl.formatMessage({ id: 'kyc.field.selfieFile.label' })}
        hint={intl.formatMessage({ id: 'kyc.field.selfieFile.hint' })}
        value={form.selfieFile}
        error={errors.selfieFile}
        onUploaded={(url) => onChange('selfieFile', url)}
      />
      <FileUpload
        kind="id_doc_front"
        label={intl.formatMessage({ id: 'kyc.field.idDocFrontFile.label' })}
        hint={intl.formatMessage({ id: 'kyc.field.idDocFrontFile.hint' })}
        value={form.idDocFrontFile}
        error={errors.idDocFrontFile}
        onUploaded={(url) => onChange('idDocFrontFile', url)}
      />
      {showBack && (
        <FileUpload
          kind="id_doc_back"
          label={intl.formatMessage({ id: 'kyc.field.idDocBackFile.label' })}
          hint={intl.formatMessage({ id: 'kyc.field.idDocBackFile.hint' })}
          value={form.idDocBackFile}
          error={errors.idDocBackFile}
          onUploaded={(url) => onChange('idDocBackFile', url)}
        />
      )}
    </section>
  );
}

function BusinessInfoStep({
  form,
  errors,
  countryOptions,
  onChange,
}: {
  form: FormState;
  errors: FormErrors;
  countryOptions: { code: string; name: string }[];
  onChange: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}): React.ReactElement {
  const intl = useIntl();
  return (
    <section className="space-y-4">
      <Field label={intl.formatMessage({ id: 'kyc.field.email.label' })} error={errors.buyerEmail}>
        <input
          type="email"
          required
          autoComplete="email"
          value={form.buyerEmail}
          onChange={(e) => onChange('buyerEmail', e.target.value)}
          className={inputClass(errors.buyerEmail)}
        />
      </Field>
      <Field
        label={intl.formatMessage({ id: 'kyc.field.legalName.label' })}
        error={errors.legalName}
      >
        <input
          type="text"
          required
          autoComplete="organization"
          value={form.legalName}
          placeholder={intl.formatMessage({ id: 'kyc.field.legalName.placeholder' })}
          onChange={(e) => onChange('legalName', e.target.value)}
          className={inputClass(errors.legalName)}
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label={intl.formatMessage({ id: 'kyc.field.formationDate.label' })}
          error={errors.formationDate}
        >
          <input
            type="date"
            required
            value={form.formationDate}
            onChange={(e) => onChange('formationDate', e.target.value)}
            className={inputClass(errors.formationDate)}
          />
        </Field>
        <Field
          label={intl.formatMessage({ id: 'kyc.field.businessTaxId.label' })}
          error={errors.businessTaxId}
          hint={intl.formatMessage({ id: 'kyc.field.businessTaxId.hint' })}
        >
          <input
            type="text"
            required
            value={form.businessTaxId}
            placeholder={intl.formatMessage({ id: 'kyc.field.businessTaxId.placeholder' })}
            onChange={(e) => onChange('businessTaxId', e.target.value)}
            className={inputClass(errors.businessTaxId)}
          />
        </Field>
      </div>
      <Field label={intl.formatMessage({ id: 'kyc.field.website.label' })} error={errors.website}>
        <input
          type="url"
          required
          autoComplete="url"
          value={form.website}
          placeholder={intl.formatMessage({ id: 'kyc.field.website.placeholder' })}
          onChange={(e) => onChange('website', e.target.value)}
          className={inputClass(errors.website)}
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label={intl.formatMessage({ id: 'kyc.field.country.label' })}
          error={errors.buyerCountry}
        >
          <CountrySelect
            options={countryOptions}
            value={form.buyerCountry}
            onChange={(v) => onChange('buyerCountry', v)}
            error={errors.buyerCountry}
          />
        </Field>
        <Field label={intl.formatMessage({ id: 'kyc.field.phone.label' })} error={errors.phone}>
          <input
            type="tel"
            autoComplete="tel"
            value={form.phone}
            placeholder={intl.formatMessage({ id: 'kyc.field.phone.placeholder' })}
            onChange={(e) => onChange('phone', e.target.value)}
            className={inputClass(errors.phone)}
          />
        </Field>
      </div>
    </section>
  );
}

function BusinessDocumentsStep({
  form,
  errors,
  onChange,
}: {
  form: FormState;
  errors: FormErrors;
  onChange: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}): React.ReactElement {
  const intl = useIntl();
  return (
    <section className="space-y-4">
      <FileUpload
        kind="incorporation_doc"
        label={intl.formatMessage({ id: 'kyc.field.incorporationDocFile.label' })}
        hint={intl.formatMessage({ id: 'kyc.field.incorporationDocFile.hint' })}
        value={form.incorporationDocFile}
        error={errors.incorporationDocFile}
        onUploaded={(url) => onChange('incorporationDocFile', url)}
      />
      <FileUpload
        kind="proof_of_ownership_doc"
        label={intl.formatMessage({ id: 'kyc.field.proofOfOwnershipDocFile.label' })}
        hint={intl.formatMessage({ id: 'kyc.field.proofOfOwnershipDocFile.hint' })}
        value={form.proofOfOwnershipDocFile}
        error={errors.proofOfOwnershipDocFile}
        onUploaded={(url) => onChange('proofOfOwnershipDocFile', url)}
      />
      <Field
        label={intl.formatMessage({ id: 'kyc.owners.proofOfAddressType.label' })}
        error={errors.proofOfAddressDocType}
      >
        <select
          required
          value={form.proofOfAddressDocType}
          onChange={(e) =>
            onChange('proofOfAddressDocType', e.target.value as FormState['proofOfAddressDocType'])
          }
          className={inputClass(errors.proofOfAddressDocType)}
        >
          <option value="">
            {intl.formatMessage({ id: 'kyc.owners.proofOfAddressType.placeholder' })}
          </option>
          {PROOF_OF_ADDRESS_TYPES.map((t) => (
            <option key={t} value={t}>
              {intl.formatMessage({ id: `kyc.owners.proofOfAddressType.${t}` })}
            </option>
          ))}
        </select>
      </Field>
      <FileUpload
        kind="proof_of_address"
        label={intl.formatMessage({ id: 'kyc.owners.proofOfAddress.label' })}
        hint={intl.formatMessage({ id: 'kyc.owners.proofOfAddress.hint' })}
        value={form.proofOfAddressDocFile}
        error={errors.proofOfAddressDocFile}
        onUploaded={(url) => onChange('proofOfAddressDocFile', url)}
      />
    </section>
  );
}

function OwnersStep({
  form,
  errors,
  countryOptions,
  onUpdate,
  onAdd,
  onRemove,
}: {
  form: FormState;
  errors: FormErrors;
  countryOptions: { code: string; name: string }[];
  onUpdate: (index: number, partial: Partial<OwnerState>) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}): React.ReactElement {
  return (
    <section className="space-y-4">
      <h2 className="text-base font-medium text-[var(--color-navy-900)]">
        <FormattedMessage id="kyc.owners.heading" />
      </h2>
      <p className="text-sm text-[var(--color-navy-600)]">
        <FormattedMessage id="kyc.owners.body" />
      </p>
      {form.owners.map((owner, index) => (
        <OwnerCard
          key={index}
          index={index}
          owner={owner}
          errors={errors}
          countryOptions={countryOptions}
          canRemove={form.owners.length > 1}
          onUpdate={(partial) => onUpdate(index, partial)}
          onRemove={() => onRemove(index)}
        />
      ))}
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-2 rounded-md border border-dashed border-[var(--color-navy-300)] px-3 py-2 text-sm text-[var(--color-navy-700)] hover:bg-[var(--color-navy-50)]"
      >
        <Plus size={16} strokeWidth={1.5} aria-hidden="true" />
        <FormattedMessage id="kyc.owners.add" />
      </button>
    </section>
  );
}

function OwnerCard({
  index,
  owner,
  errors,
  countryOptions,
  canRemove,
  onUpdate,
  onRemove,
}: {
  index: number;
  owner: OwnerState;
  errors: FormErrors;
  countryOptions: { code: string; name: string }[];
  canRemove: boolean;
  onUpdate: (partial: Partial<OwnerState>) => void;
  onRemove: () => void;
}): React.ReactElement {
  const intl = useIntl();
  const prefix = `owners.${index}.`;
  const showBack = owner.idDocType === 'ID_CARD' || owner.idDocType === 'DRIVERS';
  const isUS = owner.country.toUpperCase() === 'US';
  const err = (k: string): string | undefined => errors[`${prefix}${k}`];
  return (
    <div className="rounded-lg border border-[var(--color-navy-200)] bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-[var(--color-navy-900)]">
          <FormattedMessage id="kyc.owners.title" values={{ index: index + 1 }} />
        </h3>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-navy-200)] px-2 py-1 text-xs text-[var(--color-danger)] hover:bg-[var(--color-navy-50)]"
          >
            <Trash2 size={14} strokeWidth={1.5} aria-hidden="true" />
            <FormattedMessage id="kyc.owners.remove" />
          </button>
        )}
      </div>
      <div className="space-y-3">
        <Field label={intl.formatMessage({ id: 'kyc.owners.role.label' })} error={err('role')}>
          <select
            required
            value={owner.role}
            onChange={(e) => onUpdate({ role: e.target.value as OwnerState['role'] })}
            className={inputClass(err('role'))}
          >
            <option value="">{intl.formatMessage({ id: 'kyc.owners.role.placeholder' })}</option>
            {OWNER_ROLES.map((r) => (
              <option key={r} value={r}>
                {intl.formatMessage({ id: `kyc.owners.role.${r}` })}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label={intl.formatMessage({ id: 'kyc.field.firstName.label' })}
            error={err('firstName')}
          >
            <input
              type="text"
              required
              value={owner.firstName}
              onChange={(e) => onUpdate({ firstName: e.target.value })}
              className={inputClass(err('firstName'))}
            />
          </Field>
          <Field
            label={intl.formatMessage({ id: 'kyc.field.lastName.label' })}
            error={err('lastName')}
          >
            <input
              type="text"
              required
              value={owner.lastName}
              onChange={(e) => onUpdate({ lastName: e.target.value })}
              className={inputClass(err('lastName'))}
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label={intl.formatMessage({ id: 'kyc.field.dateOfBirth.label' })}
            error={err('dateOfBirth')}
          >
            <input
              type="date"
              required
              value={owner.dateOfBirth}
              onChange={(e) => onUpdate({ dateOfBirth: e.target.value })}
              className={inputClass(err('dateOfBirth'))}
            />
          </Field>
          <Field label={intl.formatMessage({ id: 'kyc.field.taxId.label' })} error={err('taxId')}>
            <input
              type="text"
              required
              value={owner.taxId}
              onChange={(e) => onUpdate({ taxId: e.target.value })}
              className={inputClass(err('taxId'))}
            />
          </Field>
        </div>
        {isUS && (
          <Field
            label={intl.formatMessage({ id: 'kyc.owners.taxType.label' })}
            error={err('taxType')}
          >
            <select
              required
              value={owner.taxType}
              onChange={(e) => onUpdate({ taxType: e.target.value as OwnerState['taxType'] })}
              className={inputClass(err('taxType'))}
            >
              <option value="">
                {intl.formatMessage({ id: 'kyc.owners.taxType.placeholder' })}
              </option>
              {TAX_TYPES.map((t) => (
                <option key={t} value={t}>
                  {intl.formatMessage({ id: `kyc.owners.taxType.${t}` })}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field
          label={intl.formatMessage({ id: 'kyc.field.addressLine1.label' })}
          error={err('addressLine1')}
        >
          <input
            type="text"
            required
            value={owner.addressLine1}
            onChange={(e) => onUpdate({ addressLine1: e.target.value })}
            className={inputClass(err('addressLine1'))}
          />
        </Field>
        <Field
          label={intl.formatMessage({ id: 'kyc.field.addressLine2.label' })}
          error={err('addressLine2')}
        >
          <input
            type="text"
            value={owner.addressLine2}
            onChange={(e) => onUpdate({ addressLine2: e.target.value })}
            className={inputClass(err('addressLine2'))}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={intl.formatMessage({ id: 'kyc.field.city.label' })} error={err('city')}>
            <input
              type="text"
              required
              value={owner.city}
              onChange={(e) => onUpdate({ city: e.target.value })}
              className={inputClass(err('city'))}
            />
          </Field>
          <Field
            label={intl.formatMessage({ id: 'kyc.field.stateProvinceRegion.label' })}
            error={err('stateProvinceRegion')}
          >
            <input
              type="text"
              required
              value={owner.stateProvinceRegion}
              onChange={(e) => onUpdate({ stateProvinceRegion: e.target.value })}
              className={inputClass(err('stateProvinceRegion'))}
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label={intl.formatMessage({ id: 'kyc.field.postalCode.label' })}
            error={err('postalCode')}
          >
            <input
              type="text"
              required
              value={owner.postalCode}
              onChange={(e) => onUpdate({ postalCode: e.target.value })}
              className={inputClass(err('postalCode'))}
            />
          </Field>
          <Field
            label={intl.formatMessage({ id: 'kyc.field.country.label' })}
            error={err('country')}
          >
            <CountrySelect
              options={countryOptions}
              value={owner.country}
              onChange={(v) => onUpdate({ country: v })}
              error={err('country')}
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label={intl.formatMessage({ id: 'kyc.field.idDocCountry.label' })}
            error={err('idDocCountry')}
          >
            <CountrySelect
              options={countryOptions}
              value={owner.idDocCountry}
              onChange={(v) => onUpdate({ idDocCountry: v })}
              error={err('idDocCountry')}
            />
          </Field>
          <Field
            label={intl.formatMessage({ id: 'kyc.field.idDocType.label' })}
            error={err('idDocType')}
          >
            <select
              required
              value={owner.idDocType}
              onChange={(e) => onUpdate({ idDocType: e.target.value as OwnerState['idDocType'] })}
              className={inputClass(err('idDocType'))}
            >
              <option value="">
                {intl.formatMessage({ id: 'kyc.field.idDocType.placeholder' })}
              </option>
              {ID_DOC_TYPES.map((t) => (
                <option key={t} value={t}>
                  {intl.formatMessage({ id: `kyc.field.idDocType.${t.toLowerCase()}` })}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <FileUpload
          kind="selfie"
          ownerIndex={index}
          label={intl.formatMessage({ id: 'kyc.field.selfieFile.label' })}
          hint={intl.formatMessage({ id: 'kyc.field.selfieFile.hint' })}
          value={owner.selfieFile}
          error={err('selfieFile')}
          onUploaded={(url) => onUpdate({ selfieFile: url })}
        />
        <FileUpload
          kind="id_doc_front"
          ownerIndex={index}
          label={intl.formatMessage({ id: 'kyc.field.idDocFrontFile.label' })}
          hint={intl.formatMessage({ id: 'kyc.field.idDocFrontFile.hint' })}
          value={owner.idDocFrontFile}
          error={err('idDocFrontFile')}
          onUploaded={(url) => onUpdate({ idDocFrontFile: url })}
        />
        {showBack && (
          <FileUpload
            kind="id_doc_back"
            ownerIndex={index}
            label={intl.formatMessage({ id: 'kyc.field.idDocBackFile.label' })}
            hint={intl.formatMessage({ id: 'kyc.field.idDocBackFile.hint' })}
            value={owner.idDocBackFile}
            error={err('idDocBackFile')}
            onUploaded={(url) => onUpdate({ idDocBackFile: url })}
          />
        )}
        <Field
          label={intl.formatMessage({ id: 'kyc.owners.proofOfAddressType.label' })}
          error={err('proofOfAddressDocType')}
        >
          <select
            required
            value={owner.proofOfAddressDocType}
            onChange={(e) =>
              onUpdate({
                proofOfAddressDocType: e.target.value as OwnerState['proofOfAddressDocType'],
              })
            }
            className={inputClass(err('proofOfAddressDocType'))}
          >
            <option value="">
              {intl.formatMessage({ id: 'kyc.owners.proofOfAddressType.placeholder' })}
            </option>
            {PROOF_OF_ADDRESS_TYPES.map((t) => (
              <option key={t} value={t}>
                {intl.formatMessage({ id: `kyc.owners.proofOfAddressType.${t}` })}
              </option>
            ))}
          </select>
        </Field>
        <FileUpload
          kind="proof_of_address"
          ownerIndex={index}
          label={intl.formatMessage({ id: 'kyc.owners.proofOfAddress.label' })}
          hint={intl.formatMessage({ id: 'kyc.owners.proofOfAddress.hint' })}
          value={owner.proofOfAddressDocFile}
          error={err('proofOfAddressDocFile')}
          onUploaded={(url) => onUpdate({ proofOfAddressDocFile: url })}
        />
      </div>
    </div>
  );
}

function ReviewStep({
  form,
  countryOptions,
}: {
  form: FormState;
  countryOptions: { code: string; name: string }[];
}): React.ReactElement {
  const country =
    countryOptions.find((c) => c.code === form.buyerCountry)?.name ?? form.buyerCountry;
  return (
    <section className="space-y-3">
      <h2 className="text-base font-medium text-[var(--color-navy-900)]">
        <FormattedMessage id="kyc.review.summary.heading" />
      </h2>
      <p className="text-sm text-[var(--color-navy-600)]">
        <FormattedMessage id="kyc.review.summary.body" />
      </p>
      <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <SummaryRow label="Account type" value={form.entityType} />
        <SummaryRow label="Email" value={form.buyerEmail} />
        {form.entityType === 'individual' ? (
          <>
            <SummaryRow label="Name" value={`${form.firstName} ${form.lastName}`.trim()} />
            <SummaryRow label="Date of birth" value={form.dateOfBirth} />
            <SummaryRow label="Tax ID" value={form.taxId} />
            <SummaryRow label="Selfie" value={form.selfieFile ? '✓' : '—'} />
            <SummaryRow label="ID front" value={form.idDocFrontFile ? '✓' : '—'} />
          </>
        ) : (
          <>
            <SummaryRow label="Legal name" value={form.legalName} />
            <SummaryRow label="Formation date" value={form.formationDate} />
            <SummaryRow label="Tax ID" value={form.businessTaxId} />
            <SummaryRow label="Website" value={form.website} />
            <SummaryRow label="Owners" value={String(form.owners.length)} />
            <SummaryRow label="Incorporation doc" value={form.incorporationDocFile ? '✓' : '—'} />
            <SummaryRow
              label="Proof of ownership"
              value={form.proofOfOwnershipDocFile ? '✓' : '—'}
            />
          </>
        )}
        <SummaryRow label="Country" value={country} />
        <SummaryRow
          label="Address"
          value={[
            form.addressLine1,
            form.addressLine2,
            form.city,
            form.stateProvinceRegion,
            form.postalCode,
          ]
            .filter(Boolean)
            .join(', ')}
        />
      </dl>
    </section>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-[var(--color-navy-500)]">{label}</dt>
      <dd className="mt-0.5 text-sm text-[var(--color-navy-900)]">{value || '—'}</dd>
    </div>
  );
}

function FileUpload({
  kind,
  ownerIndex,
  label,
  hint,
  value,
  error,
  onUploaded,
}: {
  kind: FileKind;
  ownerIndex?: number;
  label: string;
  hint: string;
  value: string;
  error?: string | undefined;
  onUploaded: (url: string) => void;
}): React.ReactElement {
  const intl = useIntl();
  const [status, setStatus] = useState<'idle' | 'uploading' | 'done' | 'error'>(
    value ? 'done' : 'idle',
  );
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_MIMES.includes(file.type as (typeof ALLOWED_MIMES)[number])) {
      setStatus('error');
      setLocalError(intl.formatMessage({ id: 'kyc.upload.unsupported' }));
      return;
    }
    // Narrowed: file.type is in ALLOWED_MIMES, which is the same set as
    // the API's KycUploadContentType enum.
    const contentType = file.type as (typeof ALLOWED_MIMES)[number];
    if (file.size > MAX_FILE_BYTES) {
      setStatus('error');
      setLocalError(intl.formatMessage({ id: 'kyc.upload.unsupported' }));
      return;
    }
    setStatus('uploading');
    setLocalError(null);
    try {
      const presigned: BuyerKycUploadUrlDTO = await api.createKycUploadUrl({
        kind,
        contentType,
        ...(typeof ownerIndex === 'number' ? { ownerIndex } : {}),
      });
      const headers = new Headers(presigned.uploadHeaders ?? {});
      if (!headers.has('Content-Type')) headers.set('Content-Type', file.type);
      const res = await fetch(presigned.uploadUrl, {
        method: presigned.uploadMethod,
        headers,
        body: file,
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      onUploaded(presigned.publicUrl);
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setLocalError(
        err instanceof ApiError ? err.message : intl.formatMessage({ id: 'kyc.upload.error' }),
      );
    }
  }

  const inputId = `kyc-file-${kind}-${ownerIndex ?? 'top'}`;
  return (
    <div>
      <label htmlFor={inputId} className="block text-sm font-medium text-[var(--color-navy-800)]">
        {label}
      </label>
      <p className="mt-1 text-xs text-[var(--color-navy-500)]">{hint}</p>
      <div className="mt-2 flex items-center gap-3">
        <label
          htmlFor={inputId}
          className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-[var(--color-navy-200)] bg-white px-3 py-1.5 text-sm text-[var(--color-navy-800)] hover:bg-[var(--color-navy-50)]"
        >
          {status === 'done' ? (
            <Check size={16} strokeWidth={1.5} aria-hidden="true" />
          ) : (
            <Upload size={16} strokeWidth={1.5} aria-hidden="true" />
          )}
          <FormattedMessage id={status === 'done' ? 'kyc.upload.replace' : 'kyc.upload.cta'} />
        </label>
        <span className="text-xs text-[var(--color-navy-600)]">
          {status === 'uploading' && <FormattedMessage id="kyc.upload.uploading" />}
          {status === 'done' && <FormattedMessage id="kyc.upload.uploaded" />}
        </span>
      </div>
      <input
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={(e) => void handleFile(e)}
        className="sr-only"
      />
      {(error || localError) && (
        <span className="mt-1 block text-xs text-[var(--color-danger)]" role="alert">
          {error ?? localError}
        </span>
      )}
    </div>
  );
}

function CountrySelect({
  options,
  value,
  onChange,
  error,
}: {
  options: { code: string; name: string }[];
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
}): React.ReactElement {
  const intl = useIntl();
  return (
    <select
      required
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputClass(error)}
    >
      <option value="">{intl.formatMessage({ id: 'kyc.field.country.placeholder' })}</option>
      {options.map((c) => (
        <option key={c.code} value={c.code}>
          {c.name}
        </option>
      ))}
    </select>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
}): React.ReactElement {
  return (
    <label className="block text-sm font-medium text-[var(--color-navy-800)]">
      {label}
      {children}
      {hint && (
        <span className="mt-1 block text-xs font-normal text-[var(--color-navy-500)]">{hint}</span>
      )}
      {error && (
        <span className="mt-1 block text-xs font-normal text-[var(--color-danger)]" role="alert">
          {error}
        </span>
      )}
    </label>
  );
}

function inputClass(hasError: string | undefined): string {
  const base =
    'mt-1 block w-full rounded-md border bg-white px-3 py-2 text-sm text-[var(--color-navy-900)] focus:outline-none focus:ring-2';
  return hasError
    ? `${base} border-[var(--color-danger)] focus:ring-[var(--color-danger)]`
    : `${base} border-[var(--color-navy-200)] focus:ring-[var(--color-navy-500)]`;
}

function ReviewState({
  submission,
  publicId,
}: {
  submission: BuyerKycSubmissionDTO;
  publicId: string;
}): React.ReactElement {
  return (
    <div>
      <h1 className="text-xl font-semibold text-[var(--color-navy-900)]">
        <FormattedMessage id="kyc.review.heading" />
      </h1>
      <p className="mt-2 text-sm text-[var(--color-navy-600)]">
        <FormattedMessage id="kyc.review.body" />
      </p>

      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[var(--color-navy-100)] px-3 py-1 text-xs text-[var(--color-navy-800)]">
        <span className="text-[var(--color-navy-500)]">
          <FormattedMessage id="kyc.status.label" />
        </span>
        <span className="font-mono">
          <FormattedMessage id={`kyc.status.${submission.status}`} />
        </span>
      </div>

      {submission.hostedFlowUrl && (
        <a
          href={submission.hostedFlowUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-[var(--color-navy-200)] px-3 py-1.5 text-sm text-[var(--color-navy-800)] hover:bg-[var(--color-navy-50)]"
        >
          <FormattedMessage id="kyc.hosted.cta" />
        </a>
      )}

      <div className="mt-6">
        <Link
          to="/r/$publicId/tracking"
          params={{ publicId }}
          className="inline-flex items-center gap-2 rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)]"
        >
          <FormattedMessage id="kyc.review.cta" />
        </Link>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/r/$publicId/kyc')({
  component: KycPage,
  validateSearch: KycSearch.parse,
});
